import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { buildEmbed } from '../lib/videoEmbed';
import { track } from '../lib/api';
import { trackPixelEvent } from '../lib/pixels';
import { Play, ExternalLink, Send, ChevronDown, Sparkles } from 'lucide-react';

/**
 * "See it from people like you" — country/language video reviews.
 *
 * Clean stacked card style (mobile-first):
 *   - Optional language filter chips at top
 *   - Default: only first 2 cards visible, "Load more reviews" reveals the rest
 *   - Each card: thumbnail + play overlay, language label + flag + optional
 *     "Recommended" pill, platform chip + open-original, Join Telegram CTA.
 *
 * Designed to drop in just below the global SocialProofStrip on the landing page.
 */

function VideoFrame({ platform, url }) {
  const embed = buildEmbed(platform, url);
  const [play, setPlay] = useState(false);

  if (embed.kind === 'video') {
    return (
      <div className="w-full aspect-video bg-black rounded-xl overflow-hidden border border-border">
        <video src={embed.src} controls className="w-full h-full object-cover" />
      </div>
    );
  }
  if (embed.kind === 'telegram') {
    return (
      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-border">
        {!play ? (
          <button
            type="button"
            onClick={() => setPlay(true)}
            className="absolute inset-0 flex items-center justify-center group"
            data-testid="telegram-play-overlay"
          >
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(circle at 50% 50%, rgba(34,199,255,0.18), transparent 60%), linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.85))',
              }}
            />
            <div className="relative z-10 flex flex-col items-center gap-3">
              <div className="h-14 w-14 rounded-full flex items-center justify-center cta-sheen">
                <Play className="h-6 w-6 text-white" fill="currentColor" />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-[hsl(43_92%_56%)]">
                Click to load Telegram preview
              </span>
            </div>
          </button>
        ) : (
          <iframe
            title="Telegram video"
            src={embed.src}
            className="w-full h-full"
            frameBorder="0"
            allow="encrypted-media; fullscreen"
          />
        )}
      </div>
    );
  }
  if (embed.kind === 'iframe') {
    return (
      <div className="w-full aspect-video bg-black rounded-xl overflow-hidden border border-border">
        <iframe
          title="video"
          src={embed.src}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <a
      href={embed.fallback}
      target="_blank"
      rel="noreferrer"
      className="w-full aspect-video bg-black rounded-xl overflow-hidden border border-border flex items-center justify-center text-sm text-white/80 hover:text-white"
    >
      <ExternalLink className="h-4 w-4 mr-2" /> Open video
    </a>
  );
}

const INITIAL_VISIBLE = 2; // default visible cards before "Load more"

export default function CountryVideos({ settings, recommendedKey, geo }) {
  const videos = settings?.videos || [];
  const tg = settings?.telegram_link || '#';
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' or a video.key
  const [expanded, setExpanded] = useState(false);
  const recRef = useRef(null);

  // Keep the recommended language pre-selected for visual highlight only.
  // We do NOT auto-filter the list — visitors should still see other languages.
  useEffect(() => {
    if (recommendedKey && activeFilter === 'all') {
      // no-op: filter stays "all", recommendation is just a pill on the card
    }
  }, [recommendedKey, activeFilter]);

  function handleJoin(source) {
    trackPixelEvent('Subscribe', { source });
    track({
      type: 'tg_click',
      country_code: geo?.country_code,
      country_name: geo?.country_name,
      source,
    });
  }

  // Sort: recommended first, then bd, inpk, other (then any extras alphabetically by key)
  const sortedVideos = useMemo(() => {
    const order = ['bd', 'inpk', 'other'];
    const byKey = Object.fromEntries(videos.map((v) => [v.key, v]));
    const known = order.map((k) => byKey[k]).filter(Boolean);
    const extras = videos.filter((v) => !order.includes(v.key));
    const merged = [...known, ...extras];
    if (recommendedKey) {
      const idx = merged.findIndex((v) => v.key === recommendedKey);
      if (idx > 0) {
        const [rec] = merged.splice(idx, 1);
        merged.unshift(rec);
      }
    }
    return merged;
  }, [videos, recommendedKey]);

  const filteredVideos = useMemo(() => {
    if (activeFilter === 'all') return sortedVideos;
    return sortedVideos.filter((v) => v.key === activeFilter);
  }, [sortedVideos, activeFilter]);

  const visibleVideos = useMemo(() => {
    if (expanded || activeFilter !== 'all') return filteredVideos;
    return filteredVideos.slice(0, INITIAL_VISIBLE);
  }, [filteredVideos, expanded, activeFilter]);

  const hasMore =
    activeFilter === 'all' && !expanded && filteredVideos.length > INITIAL_VISIBLE;

  return (
    <section
      id="videos"
      data-testid="country-videos-section"
      className="section-pad bg-[hsl(0_0%_4%)] relative"
    >
      <div className="container-x">
        {/* Heading */}
        <div className="max-w-3xl mb-6 sm:mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] mb-2">
            Real Reviews · Your Language
          </div>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
            See It From People Like You.
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-3">
            Auto-detected by your location. You can also switch languages manually.
          </p>
        </div>

        {/* Language filter chips (segmented control) */}
        {sortedVideos.length > 1 && (
          <div className="mb-6 sm:mb-8">
            <div
              className="inline-flex items-center gap-1 p-1 rounded-full bg-[hsl(240_6%_10%)] border border-border"
              role="tablist"
              data-testid="lang-filter"
            >
              <FilterChip
                label="All"
                active={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
                testId="lang-filter-all"
              />
              {sortedVideos.map((v) => (
                <FilterChip
                  key={v.key}
                  label={`${v.flag} ${v.language}`}
                  active={activeFilter === v.key}
                  onClick={() => setActiveFilter(v.key)}
                  testId={`lang-filter-${v.key}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Video cards */}
        <div className="grid gap-5 md:grid-cols-2">
          <AnimatePresence initial={false}>
            {visibleVideos.map((v) => {
              const isRec = v.key === recommendedKey;
              return (
                <motion.div
                  key={v.key}
                  ref={isRec ? recRef : null}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                  className={`relative rounded-2xl ${isRec ? 'gold-ring' : ''}`}
                  data-testid={`country-video-card-${v.key}`}
                >
                  <Card className="card-surface rounded-2xl overflow-hidden h-full">
                    <CardContent className="p-4 sm:p-5 space-y-4 flex flex-col h-full">
                      {/* Header: flag + language + recommended pill */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-2xl leading-none flex-shrink-0">{v.flag}</span>
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                              {v.language}
                            </div>
                            <div className="font-semibold text-base sm:text-lg truncate">
                              {v.title}
                            </div>
                          </div>
                        </div>
                        {isRec && (
                          <Badge
                            className="bg-[rgba(245,185,66,0.15)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.35)] gap-1 flex-shrink-0"
                            data-testid={`recommended-badge-${v.key}`}
                          >
                            <Sparkles className="h-3 w-3" /> For you
                          </Badge>
                        )}
                      </div>

                      {/* Video frame */}
                      <VideoFrame platform={v.platform} url={v.url} />

                      {/* Meta + open original */}
                      <div className="flex items-center justify-between text-xs">
                        <Badge
                          variant="outline"
                          className="border-border text-muted-foreground font-normal"
                        >
                          via {String(v.platform).toUpperCase()}
                        </Badge>
                        <a
                          href={v.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-muted-foreground hover:text-white inline-flex items-center gap-1"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Open original
                        </a>
                      </div>

                      {/* Telegram CTA */}
                      <a
                        href={tg}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => handleJoin(`country_card_${v.key}`)}
                        data-testid={`country-card-join-telegram-button-${v.key}`}
                        className="mt-auto"
                      >
                        <Button className="w-full cta-sheen border-0 text-white font-semibold h-11 gap-2">
                          <Send className="h-4 w-4" /> Join {v.language} Telegram
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty filter state */}
        {filteredVideos.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-10">
            No videos available.
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="mt-7 sm:mt-9 flex justify-center">
            <Button
              variant="outline"
              onClick={() => setExpanded(true)}
              data-testid="load-more-videos-btn"
              className="h-11 px-6 gap-2 border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] text-[hsl(43_92%_56%)] hover:bg-[rgba(245,185,66,0.12)] hover:text-[hsl(43_92%_56%)]"
            >
              <ChevronDown className="h-4 w-4" />
              Load more reviews
              <span className="text-[11px] text-muted-foreground">
                +{filteredVideos.length - INITIAL_VISIBLE}
              </span>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm transition-colors whitespace-nowrap ${
        active
          ? 'bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.45)]'
          : 'text-muted-foreground hover:text-white border border-transparent'
      }`}
    >
      {label}
    </button>
  );
}
